import { useCallback, useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import { DataTable } from '@/components/data-table'
import { Notice, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { ConfirmAction } from '@/components/confirm-action'
import { ApiError } from '@/lib/api'
import { ModuleEditor } from '@/features/i18n/module-editor'
import { parseScope, scopeLabel, useModules, useSaveModule, type Module } from '@/features/i18n/api'

/**
 * The module dictionary — the controlled vocabulary behind the module dropdown
 * on the Messages screen.
 *
 * It exists so a module can never be free text (D-12), and that is the whole
 * value of the screen: a typo'd module fails silently end to end. The key saves,
 * the publish succeeds, the file lands under `hoem/`, and no client ever requests
 * it. Nothing errors; a user eventually reports missing copy.
 *
 * KNOWN GAP: there is no key count per module. Nothing in the API reports it, so
 * disabling a module cannot say how much copy it affects. Deliberately not
 * guessed from the paginated catalog — a wrong number here is worse than none.
 */
export function ModulesPage() {
  // Disabled rows included: a module vanishing from the console when someone
  // takes it offline reads as data loss.
  const query = useModules(false)
  const save = useSaveModule()
  const [pendingDisable, setPendingDisable] = useState<Module | null>(null)
  const [editing, setEditing] = useState<Module | null>(null)
  const [creating, setCreating] = useState(false)

  const setEnabled = useCallback(
    async (module: Module, enabled: boolean) => {
      // The whole row goes back — an omitted field would be written as its zero
      // value, which would quietly reset `platform_scope` to unrestricted.
      await save.mutateAsync({
        id: module.id,
        input: {
          code: module.code,
          display_name: module.display_name,
          platform_scope: module.platform_scope,
          sort_weight: module.sort_weight,
          enabled,
        },
      })
      toast.success(
        enabled
          ? `${module.display_name} is back in the module dropdown.`
          : `${module.display_name} disabled. Existing keys keep it.`,
      )
    },
    [save],
  )

  const toggle = useCallback(
    async (module: Module, enabled: boolean) => {
      try {
        await setEnabled(module, enabled)
      } catch (error) {
        toast.error(error instanceof ApiError ? error.message : 'Could not update the module.')
      }
    },
    [setEnabled],
  )

  const columns = useMemo<ColumnDef<Module, unknown>[]>(
    () => [
      {
        id: 'module',
        header: 'Module',
        cell: ({ row }) => (
          <div>
            <div className="text-[13px] font-semibold">{row.original.display_name}</div>
            <code className="font-mono text-[11px] text-muted-foreground">{row.original.code}</code>
          </div>
        ),
      },
      {
        id: 'platform_scope',
        header: 'Platforms',
        enableSorting: false,
        cell: ({ row }) => {
          const restricted = parseScope(row.original.platform_scope).length > 0
          return (
            <Badge
              variant={restricted ? 'warning' : 'default'}
              title={
                restricted
                  ? 'Restricted. Keys filed under platform `all` cannot use this module — they ship to every platform, so their module has to be unrestricted.'
                  : 'Any platform may file keys here.'
              }
            >
              {scopeLabel(row.original.platform_scope)}
            </Badge>
          )
        },
      },
      {
        id: 'enabled',
        header: 'Enabled',
        enableSorting: false,
        cell: ({ row }) => {
          const module = row.original
          return (
            <Switch
              checked={module.enabled}
              disabled={save.isPending}
              aria-label={`${module.display_name} enabled`}
              onCheckedChange={(checked) => {
                if (!checked) setPendingDisable(module)
                else void toggle(module, true)
              }}
            />
          )
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button variant="secondary" size="sm" onClick={() => setEditing(row.original)}>
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [save.isPending, toggle],
  )

  return (
    <>
      <PageHeader
        title="Modules"
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus />
            Add module
          </Button>
        }
      />

      <Notice>
        A module is a <b>path segment in every published file</b>, which is why codes cannot be
        renamed and why this dictionary exists at all — a free-text module fails silently: the key
        saves, the publish succeeds, and no client ever requests the misspelt file.{' '}
        <b>Rows appear in dropdown order</b>, highest weight first.
      </Notice>

      <Card>
        <DataTable columns={columns} query={query} emptyMessage="No modules configured." />
      </Card>

      <ConfirmAction
        open={pendingDisable !== null}
        onOpenChange={(open) => !open && setPendingDisable(null)}
        title={`Disable ${pendingDisable?.display_name}?`}
        description={
          <>
            It stops being offered for new keys. Keys already filed under{' '}
            <code>{pendingDisable?.code}</code> keep their module and stay in the database — they
            simply stop being exported, and re-enabling restores them.
          </>
        }
        confirmLabel="Disable"
        destructive
        onConfirm={async () => {
          if (pendingDisable) await setEnabled(pendingDisable, false)
        }}
        onConflict={() => void query.refetch()}
      />

      {creating && <ModuleEditor open onOpenChange={(open) => !open && setCreating(false)} />}

      {editing && (
        // Keyed so switching rows rebuilds the form — `useForm` reads its
        // defaults once.
        <ModuleEditor
          key={editing.id}
          module={editing}
          open
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}
    </>
  )
}
